import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import AssistantIcon from "@material-ui/icons/Assistant";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LinkIcon from "@material-ui/icons/Link";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import ContactModal from "../ContactModal";
import ContactAvatar from "../ContactAvatar";
import ContactTagsEditor from "../ContactTagsEditor";
import ContactAlerts from "../ContactAlerts";
import useContactFieldTemplates from "../../hooks/useContactFieldTemplates";
import { splitContactFields } from "../../helpers/contactFields";
import { ContactNotes } from "../ContactNotes";
import OpportunityModal from "../OpportunityModal";
import formatPhone from "../../helpers/formatPhone";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderLeft: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
	},
	header: {
		display: "flex",
		borderBottom: `1px solid ${theme.palette.divider}`,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: 62,
		justifyContent: "flex-start",
		gap: 4,
	},
	content: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	// Topo: foto, nome e contatos centralizados
	profile: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		textAlign: "center",
		gap: 2,
		padding: "18px 16px 14px",
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	profileName: {
		fontWeight: 600,
		fontSize: "1rem",
		marginTop: 8,
		maxWidth: "100%",
	},
	profileLine: {
		fontSize: "0.78rem",
		color: theme.palette.text.secondary,
		maxWidth: "100%",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},

	section: {
		padding: "12px 16px",
		borderBottom: `1px solid ${theme.palette.divider}`,
		display: "flex",
		flexDirection: "column",
		gap: 8,
	},
	sectionTitle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		fontSize: "0.68rem",
		fontWeight: 700,
		letterSpacing: "0.07em",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
		minHeight: 24,
	},
	kv: {
		display: "grid",
		gridTemplateColumns: "96px 1fr",
		gap: "4px 8px",
		fontSize: "0.8rem",
		"& > span:nth-child(odd)": { color: theme.palette.text.secondary },
		"& > span:nth-child(even)": { fontWeight: 500, minWidth: 0, overflowWrap: "anywhere" },
	},
	queueDot: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		marginRight: 6,
	},
	aiBox: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(124,58,237,0.15)" : "rgba(124,58,237,0.07)",
		border: "1px solid rgba(124,58,237,0.25)",
		borderRadius: 10,
		padding: "9px 11px",
		fontSize: "0.8rem",
		whiteSpace: "pre-wrap",
		lineHeight: 1.55,
	},
	aiTitle: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		color: "#7C3AED",
		fontWeight: 600,
		fontSize: "0.78rem",
		marginBottom: 4,
	},

	// Editar contato: botão suave em azul claro
	editButton: {
		marginTop: 10,
		height: 32,
		padding: "0 14px",
		borderRadius: 9,
		textTransform: "none",
		fontWeight: 600,
		fontSize: "0.8rem",
		color: theme.palette.type === "dark" ? "#8DB9F5" : theme.palette.primary.main,
		backgroundColor: theme.palette.type === "dark" ? "rgba(45,123,232,0.18)" : "#EAF2FD",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(45,123,232,0.28)" : "#DCEBFF",
		},
	},
	// Ações pequenas das seções (+ Criar, Vincular)
	smallAction: {
		minWidth: 0,
		height: 26,
		padding: "0 8px",
		borderRadius: 7,
		textTransform: "none",
		fontWeight: 600,
		fontSize: "0.75rem",
		letterSpacing: 0,
	},

	contactExtraInfo: {
		padding: "6px 10px",
		borderRadius: 8,
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F0F3F8",
	},
	// Mantido para o ContactDrawerSkeleton
	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": { margin: 4 },
	},
	contactAvatar: { margin: 15, width: 100, height: 100 },
	contactDetails: { marginTop: 8, padding: 8, display: "flex", flexDirection: "column" },
}));

const statusLabels = { open: "Em atendimento", pending: "Aguardando", closed: "Resolvido" };

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const templates = useContactFieldTemplates();
	// alertas ficam na faixa vermelha do topo; aqui só os demais campos
	const { fields: drawerFields } = splitContactFields(contact?.extraInfo, templates);

	const [modalOpen, setModalOpen] = useState(false);
	const [opportunities, setOpportunities] = useState([]);
	const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
	const [linkedOpportunityId, setLinkedOpportunityId] = useState(null);

	useEffect(() => {
		setLinkedOpportunityId(ticket?.opportunityId || null);
	}, [ticket]);

	const loadOpportunities = async () => {
		if (!contact?.id) return;
		try {
			const { data } = await api.get("/opportunities", {
				params: { contactId: contact.id },
			});
			setOpportunities(data.opportunities || []);
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		if (open && contact?.id) loadOpportunities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, contact?.id]);

	const handleLinkTicket = async (opportunity, link) => {
		try {
			if (link) {
				await api.post(`/opportunities/${opportunity.id}/tickets/${ticket.id}`);
				setLinkedOpportunityId(opportunity.id);
				toast.success("Conversa vinculada ao negócio.");
			} else {
				await api.delete(`/opportunities/${opportunity.id}/tickets/${ticket.id}`);
				setLinkedOpportunityId(null);
				toast.success("Conversa desvinculada do negócio.");
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpportunityModalClose = async (saved) => {
		setOpportunityModalOpen(false);
		if (saved) {
			// negócio criado a partir da conversa já nasce vinculado a ela
			await handleLinkTicket(saved, true);
			loadOpportunities();
		}
	};

	return (
		<>
			<Drawer
				className={classes.drawer}
				variant="persistent"
				anchor="right"
				open={open}
				PaperProps={{ style: { position: "absolute" } }}
				BackdropProps={{ style: { position: "absolute" } }}
				ModalProps={{
					container: document.getElementById("drawer-container"),
					style: { position: "absolute" },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					<Typography style={{ fontWeight: 600 }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>
				{loading ? (
					<ContactDrawerSkeleton classes={classes} />
				) : (
					<div className={classes.content}>
						<div className={classes.profile}>
							<ContactAvatar src={contact.profilePicUrl} name={contact.name} style={{ width: 68, height: 68 }} />
							<Typography className={classes.profileName} noWrap>{contact.name}</Typography>
							{contact.number && (
								<span className={classes.profileLine}>
									<Link href={`tel:${contact.number}`} color="inherit">{formatPhone(contact.number)}</Link>
								</span>
							)}
							{contact.email && (
								<span className={classes.profileLine}>
									<Link href={`mailto:${contact.email}`} color="inherit">{contact.email}</Link>
								</span>
							)}
							<Button
								size="small"
								startIcon={<EditOutlinedIcon style={{ fontSize: 16 }} />}
								onClick={() => setModalOpen(true)}
								className={classes.editButton}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
						</div>

						<ContactAlerts contact={contact} style={{ margin: "12px 16px 0" }} />

						{ticket?.id && (
							<div className={classes.section}>
								<div className={classes.sectionTitle}>Atendimento</div>
								<div className={classes.kv}>
									<span>Departamento</span>
									<span>
										<span
											className={classes.queueDot}
											style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
										/>
										{ticket.queue?.name || "Sem departamento"}
									</span>
									<span>Atendente</span>
									<span>{ticket.user?.name || "Ninguém ainda"}</span>
									{ticket.whatsapp?.name && (
										<>
											<span>Conexão</span>
											<span>{ticket.whatsapp.name}</span>
										</>
									)}
									<span>Ticket</span>
									<span>#{ticket.id}{statusLabels[ticket.status] ? ` · ${statusLabels[ticket.status]}` : ""}</span>
								</div>
							</div>
						)}

						<div className={classes.section}>
							<div className={classes.sectionTitle}>Etiquetas do contato</div>
							<ContactTagsEditor contactId={contact.id} />
						</div>

						{ticket?.aiSummary && (
							<div className={classes.section}>
								<div className={classes.aiBox}>
									<div className={classes.aiTitle}>
										<AssistantIcon style={{ fontSize: 16 }} />
										Resumo da IA
									</div>
									{ticket.aiSummary}
								</div>
							</div>
						)}

						<div className={classes.section}>
							<div className={classes.sectionTitle}>
								{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
							</div>
							<ContactNotes ticket={ticket} />
						</div>

						<div className={classes.section}>
							<div className={classes.sectionTitle}>
								Negócios
								<Button
									size="small"
									color="primary"
									onClick={() => setOpportunityModalOpen(true)}
									className={classes.smallAction}
								>
									+ Criar
								</Button>
							</div>
							{opportunities.length === 0 ? (
								<Typography variant="body2" color="textSecondary" style={{ fontSize: "0.8rem" }}>
									Nenhum negócio com este contato.
								</Typography>
							) : (
								opportunities.map(opportunity => (
									<div key={opportunity.id} className={classes.contactExtraInfo}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: 6,
												flexWrap: "wrap",
											}}
										>
											<Chip
												size="small"
												label={opportunity.stage?.name}
												style={{
													backgroundColor: `${opportunity.stage?.color || "#9E9E9E"}33`,
													color: opportunity.stage?.color,
													fontWeight: 600,
												}}
											/>
											<Typography
												variant="body2"
												style={{ fontWeight: 600, flex: 1, minWidth: 0 }}
												noWrap
											>
												{opportunity.title}
											</Typography>
										</div>
										{opportunity.value != null && opportunity.value !== "" && (
											<Typography variant="caption" color="textSecondary">
												{Number(opportunity.value).toLocaleString("pt-BR", {
													style: "currency",
													currency: "BRL",
												})}
											</Typography>
										)}
										<div style={{ marginTop: 4 }}>
											{linkedOpportunityId === opportunity.id ? (
												<Button
													size="small"
													startIcon={<LinkOffIcon style={{ fontSize: 15 }} />}
													onClick={() => handleLinkTicket(opportunity, false)}
													className={classes.smallAction}
												>
													Desvincular conversa
												</Button>
											) : (
												<Button
													size="small"
													color="primary"
													startIcon={<LinkIcon style={{ fontSize: 15 }} />}
													onClick={() => handleLinkTicket(opportunity, true)}
													className={classes.smallAction}
												>
													Vincular conversa
												</Button>
											)}
										</div>
									</div>
								))
							)}
						</div>

						<div className={classes.section} style={{ borderBottom: 0 }}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							></ContactModal>
							<OpportunityModal
								open={opportunityModalOpen}
								onClose={handleOpportunityModalClose}
								initialContact={contact}
							/>
							<div className={classes.sectionTitle}>
								{i18n.t("contactDrawer.extraInfo")}
							</div>
							{drawerFields.length === 0 && (
								<Typography variant="body2" color="textSecondary" style={{ fontSize: "0.8rem" }}>
									Nenhuma informação adicional.
								</Typography>
							)}
							{drawerFields.map(info => (
								<div key={info.id || info.name} className={classes.contactExtraInfo}>
									<InputLabel style={{ fontSize: "0.72rem" }}>{info.name}</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2, fontSize: "0.82rem" }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</div>
							))}
						</div>
					</div>
				)}
			</Drawer>
		</>
	);
};

export default ContactDrawer;
